import {Dispatch, SetStateAction, useRef, useState} from "react";
import {Calendar} from "react-date-range";
import styled from "styled-components";
import theme from "../../style/theme";
import {dateFormat} from "../../utils/date/date-format";
import CalendarLineIcon from "../icons/CalendarLineIcon/CalendarLineIcon";
import {useClickOutside} from "../../utils/click/handle-click";

interface Props {
    selectedDate: Date;
    setSelectedDate: Dispatch<SetStateAction<Date>>;
}

export const BasicCalendar: React.FC<Props> = (
    {
        selectedDate,
        setSelectedDate,
    }
) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const handleClickBox = () => {
        setOpen(!open);
    };

    useClickOutside(ref, () => setOpen(false));

    return (
        <Container ref={ref}>
            <BasicCalendarContainer onClick={handleClickBox} open={open}>
                <IconContainer>
                    <CalendarLineIcon
                        width="1.125rem"
                        height="1.125rem"
                        fill={theme.palette.primitives.gray500}
                    />
                </IconContainer>
                <DateContainer>{dateFormat(selectedDate)}</DateContainer>
            </BasicCalendarContainer>
            {open && (
                <CalendarContainer>
                    <Calendar
                        editableDateInputs={true}
                        date={selectedDate}
                        onChange={(item) => setSelectedDate(item)}
                        color={theme.palette.primitives.yellow400}
                    />
                </CalendarContainer>
            )}
        </Container>
    );
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

interface BasicCalendarContainerProps {
    open: boolean;
}

const BasicCalendarContainer = styled.div<BasicCalendarContainerProps>`
  width: 10.625rem;
  height: 2.625rem;
  display: flex;
  align-items: center;
  border-radius: 0.25rem;
  background: ${theme.palette.primitives.white};
  border: 1px solid ${(props) =>
          props.open
                  ? theme.palette.primitives.gray600
                  : theme.palette.primitives.gray200};
`;

const IconContainer = styled.div`
  margin: 0.2rem 0.5rem 0 0.75rem;
`;

const DateContainer = styled.div`
  font-weight: 500;
  font-size: 0.938rem;
  line-height: 1.125rem;
  letter-spacing: 0.04rem;
  color: ${theme.palette.primitives.gray900};
`;

const CalendarContainer = styled.div`
  position: absolute;
  margin-top: 2.5rem;

  .rdrDayToday .rdrDayNumber span:after {
    background-color: ${theme.palette.primitives.yellow400};
  }

  .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,
  .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span,
  .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span,
  .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span {
    color: black;
  }

  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
`;
